import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, ChevronsUpDown, Check, Plus } from 'lucide-react';
import { Project } from '../../../types/domain';
import { PortalPopover } from '../../ui/PortalPopover';
import { Button } from '../../ui/Button';

interface ProjectSwitcherDropdownProps {
  projects: Project[];
  activeProject: Project | null;
  currentOrgId?: string;
  canManageOrg: boolean;
  onSelectProject: (project: Project) => void;
  onOpenCreateProject: () => void;
}

export const ProjectSwitcherDropdown: React.FC<ProjectSwitcherDropdownProps> = ({
  projects,
  activeProject,
  currentOrgId,
  canManageOrg,
  onSelectProject,
  onOpenCreateProject,
}) => {
  const [projectOpen, setProjectOpen] = useState(false);
  const projectButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  return (
    <div className="relative shrink-0">
      <Button
        ref={projectButtonRef as any}
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => setProjectOpen(!projectOpen)}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-cf-surfaceActive font-medium text-cf-textPrimary transition-colors h-auto"
        title="Switch Active Project"
      >
        <FolderKanban size={13} className="text-cf-orange flex-shrink-0" />
        <span className="max-w-[75px] sm:max-w-[140px] truncate">
          {activeProject?.name || 'Select Project'}
        </span>
        <ChevronsUpDown size={11} className="text-cf-textMuted flex-shrink-0" />
      </Button>

      <PortalPopover
        isOpen={projectOpen}
        onClose={() => setProjectOpen(false)}
        triggerRef={projectButtonRef}
        align="left"
        className="w-64 bg-cf-surface border border-cf-border rounded-lg shadow-xl overflow-hidden text-xs"
      >
        <div className="px-3 py-2 text-xs uppercase text-cf-textMuted border-b border-cf-border flex items-center justify-between font-medium">
          <span>Projects ({projects.length})</span>
        </div>

        <div className="max-h-48 overflow-y-auto">
          {projects.map((p) => {
            const isSelected = p.id === activeProject?.id;
            return (
              <Button
                key={p.id}
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => {
                  onSelectProject(p);
                  setProjectOpen(false);
                  navigate(`/organization/${currentOrgId || ''}/projects/${p.id}`);
                }}
                title={p.name}
                className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-cf-surfaceActive h-auto rounded-none ${
                  isSelected ? 'text-cf-orange font-medium bg-cf-surfaceActive/60' : 'text-cf-textPrimary'
                }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  <FolderKanban
                    size={13}
                    className={isSelected ? 'text-cf-orange flex-shrink-0' : 'text-cf-textMuted flex-shrink-0'}
                  />
                  <span className="truncate">{p.name}</span>
                </div>
                {isSelected && <Check size={12} className="text-cf-orange flex-shrink-0 ml-2" />}
              </Button>
            );
          })}
        </div>

        {canManageOrg && (
          <div className="border-t border-cf-border">
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => {
                setProjectOpen(false);
                onOpenCreateProject();
              }}
              leftIcon={<Plus size={13} />}
              className="w-full justify-start px-3 py-2 text-cf-orange hover:bg-cf-orange/10 text-xs transition-colors font-medium h-auto rounded-none"
            >
              Create New Project
            </Button>
          </div>
        )}
      </PortalPopover>
    </div>
  );
};
